import React from 'react';
import {useLocation} from "react-router-dom";
import { useAsync } from 'react-async-hook';
import TextareaAutosize from 'react-textarea-autosize';
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { taskApi } from '../../../apis/task/taskApi';
import { formatDate, formatOmittedDate } from '../../../libs/utils/formatDate';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { TaskCommentIndex } from '../TaskComment/TaskCommentIndex';
import { TaskChecklistIndex } from '../TaskChecklist/TaskChecklistIndex';
import { UpdateTaskAssignUserModal } from '../TaskAssignUser/UpdateTaskAssignUserModal';
import { UpdateTaskRoomLabelModal } from '../TaskRoomLabel/UpdateTaskRoomLabelModal';
import { getLabelColor } from '../../../libs/utils/getLabelColor';
import icon01 from '../../../assets/images/01icon_checkbox_glay.png';
import icon02 from '../../../assets/images/02icon_checkbox.png';
import icon403 from '../../../assets/images/5_ROOM_TaskBoard_Re4_03.png';
import icon407 from '../../../assets/images/5_ROOM_TaskBoard_Re4_07.png';
import icon413 from '../../../assets/images/5_ROOM_TaskBoard_Re4_13.png';
import icon415 from '../../../assets/images/5_ROOM_TaskBoard_Re4_15.png';
import { Task } from '../../../types/types';
import { UpdateTaskCalendarModal } from '../TaskCalendar/UpdateTaskCalendarModal';
import useLoading from '../../../hooks/useLoading';
import icon442 from '../../../assets/images/ROOM_TaskBoard_Re4_42.png';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateTaskModalProps {
  taskListId: number;
  onSubmitted: () => Promise<void>;
  listName: string;
  roomUniqueNo: number;
}

interface FormValuesOfName {
  name: string;
}
interface FormValuesOfContent {
  content: string;
}

export const UpdateTaskModal = (props: UpdateTaskModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const { success, danger } = useSystemMessage();
  const { taskListId, onSubmitted, listName, roomUniqueNo } = props;
  const [taskList, setTaskList] = React.useState<Task>();
  const [showCalendarModal, setShowCalendarModal] = React.useState<boolean>(
    false
  );
  const [showAssignUserModal, setShowAssignUserModal] = React.useState<boolean>(
    false
  );
  const [showRoomLabelModal, setShowRoomLabelModal] = React.useState<boolean>(
    false
  );

  const { execute: reloadTask } = useAsync(async () => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await taskApi.show(taskListId, {});
          setTaskList(response.task);
        } catch(e) {
          await logError(
            location.pathname,
            'タスクの取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, [taskListId]);

  const handleNameChange = async (data: FormValuesOfName) => {
    if (taskList === undefined) {
      await danger('タスクが存在しません。');
      return;
    }

    // 値が変更されていない場合は処理を行わない
    if (data.name === taskList.name) {
      return;
    }

    withLoading(async () => {
      try {
        await taskApi.update(
          taskList.id,
          {
            name: data.name,
          },
          {}
        );
        await success('タスク名を更新しました');
        await reloadTask();
      } catch(e) {
        await logError(
          location.pathname,
          'タスクの更新に失敗しました。',
          e
        );

        goto('internalServerError');
      }
    });
  };

  const handleContentChange = async (data: FormValuesOfContent) => {
    if (taskList === undefined) {
      await danger('タスクが存在しません。');
      return;
    }

    // 値が変更されていない場合は処理を行わない
    if ((data.content === null || data.content === "") && (taskList.content === null || taskList.content === "")) {
      return;
    }
    if (data.content === taskList.content) {
      return;
    }

    await withLoading(async () => {
      try {
        await taskApi.update(
          taskList.id,
          {
            name: taskList.name,
            content: data.content,
          },
          {}
        );
        await success('タスク説明文を更新しました');
        await reloadTask();
      } catch(e) {
        await logError(
          location.pathname,
          'タスクの更新に失敗しました。',
          e
        );

        goto('internalServerError');
      }
    });
  };

  const handleAssignUserClick = async () => {
    await reloadTask();
    setShowAssignUserModal(true);
  };

  const handleRoomLabelClick = async () => {
    await reloadTask();
    setShowRoomLabelModal(true);
  };

  const handleCalendarClick = async () => {
    await reloadTask();
    setShowCalendarModal(true);
  };

  const handleAssignUserSubmit = async () => {
    await reloadTask();
    setShowAssignUserModal(false);
  };

  const handleRoomLabelSubmit = async () => {
    await reloadTask();
    setShowRoomLabelModal(false);
  };

  const handleCalendarSubmit = async () => {
    await reloadTask();
    setShowCalendarModal(false);
  };

  const handleRoomOutputFlgToggle = async () => {
    if (taskList === undefined) {
      await danger('タスクが存在しません');
      return;
    }

    try {
      await taskApi.update(
        taskList.id,
        { name: taskList.name, roomOutputFlg: !taskList.roomOutputFlg },
        {}
      );
      await success('タスクを更新しました');
      await reloadTask();
    } catch(e) {
      await logError(
        location.pathname,
        'タスクの更新に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const handleEndFlgToggle = async () => {
    if (taskList === undefined) {
      await danger('タスクが存在しません');
      return;
    }

    try {
      await taskApi.update(
        taskList.id,
        { name: taskList.name, endFlg: !taskList.endFlg },
        {}
      );
      await success('タスクを更新しました');
      await reloadTask();
    } catch(e) {
      await logError(
        location.pathname,
        'タスクの更新に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  return (
    <>
      {taskList && (
        <>
          {showAssignUserModal && (
            <UpdateTaskAssignUserModal
              {...{ task: taskList, onSubmitted: handleAssignUserSubmit }}
            />
          )}
          {showRoomLabelModal && (
            <UpdateTaskRoomLabelModal
              {...{ task: taskList, onSubmitted: handleRoomLabelSubmit }}
            />
          )}
          <ReactModal
            isOpen
            ariaHideApp={false}
            onRequestClose={() => onSubmitted()}
            className="task-modal"
            overlayClassName="overlay"
            shouldCloseOnOverlayClick
          >
            <div className="modal-container">
              <div className="pb-4 px-4 border-b border-primary">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <p className="text-sm font-medium mb-2 break-all">
                      {listName}
                      <span className="mx-2">｜</span>
                      {`#${`00${roomUniqueNo}`.slice(-2)}`}
                      <span className="mx-2">｜</span>
                      <span className="text-xs font-light">
                        {`作成: ${formatOmittedDate(
                          taskList.createDateTime
                        )} 作成者: ${taskList.createUser}`}
                      </span>
                    </p>
                    <form
                      onBlur={handleSubmit(handleNameChange)}
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <TextareaAutosize
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('name', {
                          required: true,
                          maxLength: 100,
                        })}
                        maxRows={3}
                        defaultValue={taskList.name}
                        placeholder="タスク名を入力してください。"
                        className="p-1 text-2xl text-primary font-semibold focus:outline-primary w-full"
                      />
                      {errors.name && errors.name.type === 'required' && (
                        <span className="my-2 text-red-500 text-xs">
                          タスク名は必須です
                        </span>
                      )}
                      {errors.name && errors.name.type === 'maxLength' && (
                        <span className="my-2 text-red-500 text-xs">
                          タスク名は100文字以下で指定してください
                        </span>
                      )}
                    </form>
                  </div>
                  <div className="flex-none">
                    <button
                      type="button"
                      className="p-1 bg-transparent border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                      onClick={() => onSubmitted()}
                    >
                      <img src={icon403} alt="icon" />
                    </button>
                  </div>
                </div>
                <form onBlur={handleSubmit(handleContentChange)}>
                  <TextareaAutosize
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('content', { maxLength: 10000 })}
                    maxRows={10}
                    defaultValue={taskList.content}
                    placeholder="タスクの内容を記載してください。"
                    className="text-sm focus:outline-primary w-full p-1"
                  />
                  {errors.content && errors.content.type === 'maxLength' && (
                    <span className="my-2 text-red-500 text-xs">
                      タスク内容は10,000文字以下で指定してください
                    </span>
                  )}
                </form>
              </div>
              <div className="pt-4 px-4">
                <div className="pb-2 flex justify-between">
                  <label htmlFor="task-date" className="flex items-center">
                    <div className="text-xs font-semibold text-primary flex-none w-24 flex items-center">
                      <img src={icon407} alt="icon" />
                      <div className="ml-2">期間</div>
                    </div>
                    {taskList.startTime && taskList.endTime ? (
                      <>
                        <div className="text-sm flex items-center">
                          <button
                            type="button"
                            className="outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                            onClick={handleCalendarClick}
                          >
                            {formatDate(taskList.startTime)}
                          </button>
                          <span>〜</span>
                          <button
                            type="button"
                            className="outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                            onClick={handleCalendarClick}
                          >
                            {formatDate(taskList.endTime)}
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        className="btn-primary rounded-md text-sm"
                        type="button"
                        onClick={handleCalendarClick}
                      >
                        期間を設定
                      </button>
                    )}
                    {showCalendarModal && (
                      <UpdateTaskCalendarModal
                        {...{ taskList, onSubmitted: handleCalendarSubmit }}
                      />
                    )}
                  </label>
                  <div className="flex flex-col items-end">
                    <div className="text-primary text-xs font-semibold flex items-center mb-4">
                      ROOMに表示
                      <button
                        type="button"
                        onClick={handleRoomOutputFlgToggle}
                        className="outline-none focus:outline-none"
                      >
                        {taskList.roomOutputFlg ? (
                          <img src={icon02} alt="icon" className="ml-2" />
                        ) : (
                          <img src={icon01} alt="icon" className="ml-2" />
                        )}
                      </button>
                    </div>
                    <div className="text-primary text-xs font-semibold flex items-center">
                      タスクの完了
                      <button
                        type="button"
                        onClick={handleEndFlgToggle}
                        className="outline-none focus:outline-none"
                      >
                        {taskList.endFlg ? (
                          <img src={icon02} alt="icon" className="ml-2" />
                        ) : (
                          <img src={icon01} alt="icon" className="ml-2" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <label
                    htmlFor="task-assign-user"
                    className="flex items-center"
                  >
                    <div className="text-xs font-semibold text-primary flex-none w-24 flex items-center">
                      <img src={icon413} alt="icon" />
                      <div className="ml-2">担当者</div>
                    </div>
                    <div className="flex-grow">
                      {taskList.assignUser === undefined ? (
                        <button
                          type="button"
                          className="btn-primary rounded-md text-sm"
                          onClick={handleAssignUserClick}
                        >
                          担当を設定
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="flex flex-col items-center outline-none focus:outline-none"
                          onClick={handleAssignUserClick}
                        >
                          <img src={icon442} alt="icon" className="w-12" />
                          <p className="text-xs w-24">{taskList.assignUser}</p>
                        </button>
                      )}
                    </div>
                  </label>
                </div>
                <div className="py-2">
                  <label
                    htmlFor="task-assign-user"
                    className="flex items-center"
                  >
                    <div className="text-xs font-semibold text-primary flex-none w-24 flex items-center">
                      <img src={icon415} alt="icon" />
                      <div className="ml-2">ラベル</div>
                    </div>
                    <div className="flex-grow flex flex-wrap">
                      {taskList.taskLabelList.length > 0 ? (
                        taskList.taskLabelList.map((l) => (
                          <button
                            key={l.id}
                            type="button"
                            className="flex items-center outline-none focus:outline-none mb-2"
                            onClick={handleRoomLabelClick}
                          >
                            <div
                              className={`label-item-10 ${getLabelColor(
                                l.color
                              )}`}
                            >
                              {l.name}
                            </div>
                          </button>
                        ))
                      ) : (
                        <button
                          type="button"
                          className="btn-primary rounded-md text-sm"
                          onClick={handleRoomLabelClick}
                        >
                          ラベルを設定
                        </button>
                      )}
                    </div>
                  </label>
                </div>
                <div className="py-2">
                  <TaskChecklistIndex {...{ taskId: taskList.id }} />
                </div>
              </div>
              <div className="pt-2">
                <TaskCommentIndex {...{ taskId: taskList.id }} />
              </div>
            </div>
          </ReactModal>
        </>
      )}
    </>
  );
};
